import {Form, InputOnChangeData, Loader, Message, Table} from "semantic-ui-react"
import {Charge} from "../model/BookingTypes"
import {
  autoChargeDiscount,
  centsToString,
  extraChargesTotal,
  floor10cents,
  formatMoney,
  parseMoney,
} from "../model/charges"
import {
  BookerFragFragment,
  BookingDetails,
  ChargeItemFragment,
  Charges as CHARGES,
  ChargesFragment,
  ChargeType,
  StripeCardFragment,
} from "../graphql/queries"
import * as React from "react"
import {CSSProperties} from "react"
import {WithModalStore} from "../App"
import {MapState} from "./BookingDetails"
import {AnyExtraCharge, extraChargesLabel} from "./ExtraChargeList"
import {GraphQL} from "../graphql"
import {ID} from "../model/id"

function chargeTypeDescription(type: ChargeType): string {
  switch (type) {
    case ChargeType.MINIMUM:
      return "Minimum price"
    case ChargeType.BOOKING_FEE:
      return "Booking fee"
    case ChargeType.DISTANCE:
      return "Distance price"
    case ChargeType.EXTRA_STOPS:
      return "Extra stops"
    case ChargeType.PEOPLE_MOVER:
      return "People mover"
    case ChargeType.LNS:
      return "Late night (after 10pm)"
    case ChargeType.AIRPORT_PICKUP:
      return "Airport pickup"
    case ChargeType.BABY_OR_BOOSTER:
      return "One or more baby or booster seats"
    case ChargeType.TRIP_LEVY:
      return "Passenger Trip Levy"
  }
}

function chargeItemToCharge(item: ChargeItemFragment): Charge {
  return {
    amount: item.amount,
    description: chargeTypeDescription(item.type),
  }
}

function ChargesRow({
  charge,
  bold = false,
  strikeThrough = false,
}: {
  charge: Charge
  bold?: boolean
  strikeThrough?: boolean
}) {
  const style: CSSProperties = {}
  if (bold) {
    style.fontWeight = "bold"
  }
  if (strikeThrough) {
    style.textDecoration = "line-through"
  }

  return (
    <Table.Row style={style}>
      <Table.Cell>{charge.description}</Table.Cell>
      <Table.Cell textAlign="right">{formatMoney(charge.amount)}</Table.Cell>
    </Table.Row>
  )
}

function PriceDelta({
  charges,
  bookingPrice,
}: {
  charges: ChargesFragment | null
  bookingPrice: Charge | null
}) {
  if (charges && bookingPrice && bookingPrice.amount !== charges.total.amount) {
    const delta = bookingPrice.amount - charges.total.amount
    const style = delta > 0 ? {color: "green"} : {color: "red"}
    return (
      <span style={style}>
        {delta > 0 ? "+" : ""}
        {centsToString(delta)}
      </span>
    )
  }
  return null
}

interface OuterProps extends WithModalStore {
  forBooker: BookerFragFragment | null
  journeyId: ID
  bookingId?: ID
  booking: BookingDetails
  initialLoyaltyProgramDiscount: boolean
  giftLoyaltyProgramDiscount: boolean
  stripeCard: StripeCardFragment | null
  state: ChargesOuterState
  mapState: MapState<ChargesOuterState> | null
  isAdmin: boolean
  isInvoiced?: boolean
  extraCharges: Array<AnyExtraCharge>
  onApprovePrice: ((price: Charge) => void) | null
}

export interface ChargesOuterState {
  price: {
    amount: number
    description: string
  } | null
  editingPrice: boolean
}

interface AllProps extends OuterProps, ChargesProp {
  loading: boolean
}

interface InnerState {
  showDetails: boolean
  formPrice: {
    amount: string
    description: string
  }
}

class Charges extends React.Component<AllProps, InnerState> {
  constructor(props: AllProps) {
    super(props)

    this.state = {
      showDetails: false,
      formPrice: {
        amount: "",
        description: "",
      },
    }
  }

  handleEdit = (mapState: MapState<ChargesOuterState>) => () => {
    mapState((state) => ({...state, editingPrice: true}))
    const {price} = this.props.state

    const total = price ? price : this.chargesTotal()
    this.setState({
      formPrice: {
        amount: total ? centsToString(total.amount) : "",
        description: total ? total.description : "",
      },
    })
  }

  handleSave = (mapState: MapState<ChargesOuterState>) => () => {
    const {formPrice} = this.state

    // noinspection JSUnusedLocalSymbols
    mapState((state) => ({
      price: {
        amount: parseMoney(formPrice.amount)!,
        description: formPrice.description,
      },
      editingPrice: false,
    }))
  }

  handleSet = (mapState: MapState<ChargesOuterState>) => () => {
    mapState((state) => ({
      ...state,
      price: this.chargesTotal(),
    }))
  }

  handleReset = (mapState: MapState<ChargesOuterState>) => () => {
    mapState((state) => ({
      ...state,
      editingPrice: false,
    }))
  }

  handleApprovePrice = (onApprovePrice: (price: Charge) => void) => () => {
    onApprovePrice(this.chargesTotal()!)
  }

  handleAmount = (e: any, field: InputOnChangeData) => {
    this.setState((state) => ({
      formPrice: {...state.formPrice, amount: field.value},
    }))
  }

  handleDescription = (e: any, field: InputOnChangeData) => {
    this.setState((state) => ({
      formPrice: {...state.formPrice, description: field.value},
    }))
  }

  handleShowDetails = () => {
    // We use this.state.showDetails directly rather than giving a function to this.setState()
    // because we want the new value to reflect the current render of the form. If the user
    // clicks the button twice before a rerender, we don't want the second click to negate the
    // first.
    this.setState({showDetails: !this.state.showDetails})
  }

  chargesTotal = () => (this.props.charges ? this.props.charges.total : null)

  // See matching backend function of same name in fetchers.kt
  hasFixedPrice = () => {
    const {
      state: {price},
      charges,
    } = this.props

    if (price === null) {
      return false
    }
    if (charges === null) {
      return true
    }
    return Math.abs(charges.total.amount - price.amount) > 99
  }

  render() {
    const {
      isAdmin,
      isInvoiced,
      charges,
      mapState,
      state: {editingPrice: editing, price: bookingPrice},
      forBooker: booker,
      extraCharges,
      stripeCard,
      initialLoyaltyProgramDiscount,
      giftLoyaltyProgramDiscount,
      onApprovePrice,
      loading,
    } = this.props
    const isParentEdit = mapState !== null

    const {formPrice, showDetails} = this.state
    const total = this.chargesTotal()

    const canEdit = isAdmin && !isInvoiced && isParentEdit && !editing
    const canSave = isAdmin && editing
    const saveDisabled = parseMoney(formPrice.amount) === null
    const canApprove = isAdmin && !isParentEdit && !editing && !bookingPrice && total
    const canSet = canEdit && total && (!bookingPrice || bookingPrice.amount !== total.amount)

    // TODO Get this hardcoded 10 from the backend
    const loyaltyProgramDiscount = initialLoyaltyProgramDiscount ? 10 : 0

    const setButtonName = "Approve " + (total ? centsToString(total.amount) : "")
    const price = bookingPrice ? bookingPrice : total

    return (
      <>
        {canSave ? (
          <>
            <Form.Group>
              <Form.Field required>
                <label>Amount</label>
                <Form.Input
                  placeholder="Amount"
                  value={formPrice.amount}
                  onChange={this.handleAmount}
                />
              </Form.Field>
            </Form.Group>
            <Form.Field required>
              <label>Description</label>
              <Form.Input
                placeholder="Description"
                value={formPrice.description}
                onChange={this.handleDescription}
              />
            </Form.Field>
          </>
        ) : (
          <Form.Field>
            <label>
              Price
              {isAdmin && (
                <>
                  {" "}
                  <PriceDelta charges={charges} bookingPrice={bookingPrice} />
                </>
              )}
              <Loader inline active={loading} />
            </label>
            {!price ? (
              <div>
                To be advised.
                <br />
                Register your credit card to get {`${autoChargeDiscount}`}% discount on the
                confirmed price.
              </div>
            ) : (
              <div>
                <PriceTotal
                  extraCharges={extraCharges}
                  price={price.amount}
                  description={price.description}
                  autoCharge={stripeCard !== null}
                  loyaltyDiscount={booker ? booker.loyaltyDiscount : 0}
                  loyaltyProgramDiscount={loyaltyProgramDiscount}
                  giftLoyaltyProgramDiscount={giftLoyaltyProgramDiscount}
                />
                {this.hasFixedPrice() && (
                  <Message
                    content={
                      <p>
                        This price has been set manually by C U Soon and is subject to change if you
                        edit the Booking Details.
                      </p>
                    }
                  />
                )}
              </div>
            )}
          </Form.Field>
        )}
        {removeIfEmpty(
          <Form.Group>
            {canApprove &&
              onApprovePrice && (
                <Form.Button
                  positive
                  size="tiny"
                  onClick={this.handleApprovePrice(onApprovePrice)}
                  content="Approve"
                />
              )}
            {canSet &&
              mapState && (
                <Form.Button
                  size="tiny"
                  onClick={this.handleSet(mapState)}
                  content={setButtonName}
                />
              )}
            {canEdit &&
              mapState && (
                <Form.Button size="tiny" onClick={this.handleEdit(mapState)} content="Edit Price" />
              )}
            {canSave &&
              mapState && (
                <Form.Button
                  disabled={saveDisabled}
                  size="tiny"
                  onClick={this.handleSave(mapState)}
                  content="Save"
                />
              )}
            {canSave &&
              mapState && (
                <Form.Button size="tiny" onClick={this.handleReset(mapState)} content="Reset" />
              )}
            {isAdmin &&
              charges && (
                <Form.Button
                  size="tiny"
                  icon={showDetails ? "chevron up" : "chevron down"}
                  onClick={this.handleShowDetails}
                  content={showDetails ? "Hide details" : "Show details"}
                />
              )}
          </Form.Group>,
        )}
        {showDetails && charges && charges.items ? (
          <Table compact="very" unstackable>
            <Table.Body>
              {charges.items.map((charge, index) => (
                <ChargesRow key={index} charge={chargeItemToCharge(charge)} />
              ))}
              <ChargesRow
                key={-1}
                charge={{
                  amount: charges.items.reduce((acc, charge) => acc + charge.amount, 0),
                  description: "Subtotal",
                }}
                bold
              />
            </Table.Body>
          </Table>
        ) : null}
      </>
    )
  }
}

function removeIfEmpty<T extends React.ReactElement<{children?: React.ReactNode}>>(
  element: T | null,
): T | null {
  if (!element) {
    return null
  }
  const {children} = element.props
  if (!children) {
    return null
  }
  if (children instanceof Array && children.every((x) => !x)) {
    return null
  }
  return element
}

function PriceTotal(params: {
  description: string
  price: number
  loyaltyProgramDiscount: number
  giftLoyaltyProgramDiscount: boolean
  autoCharge: boolean
  loyaltyDiscount: number
  extraCharges: Array<AnyExtraCharge>
}) {
  const extraChargesAmount = extraChargesTotal(params.extraCharges)
  const subTotal = params.price + extraChargesAmount
  const loyaltyProgramDiscountAmount =
    floor10cents(-subTotal * (params.loyaltyProgramDiscount / 100))
  const loyaltyDiscountAmount = floor10cents(-subTotal * (params.loyaltyDiscount / 100))
  const autoChargeDiscountAmount = floor10cents(-subTotal * (autoChargeDiscount / 100))
  let total = subTotal
  total += params.giftLoyaltyProgramDiscount ? 0 : loyaltyProgramDiscountAmount
  total += loyaltyDiscountAmount
  if (params.autoCharge) {
    total += autoChargeDiscountAmount
  }
  return (
    <Table compact="very" unstackable>
      <Table.Body>
        <ChargesRow
          charge={{
            description: params.description,
            amount: params.price,
          }}
        />
        {extraChargesAmount !== 0 && (
          <ChargesRow
            charge={{
              description: extraChargesLabel(params.extraCharges),
              amount: extraChargesAmount,
            }}
          />
        )}
        {params.loyaltyProgramDiscount ? (
          <ChargesRow
            charge={{
              description: `${
                params.giftLoyaltyProgramDiscount ? "Give to charity" : "Less"
              } Loyalty Program discount (${params.loyaltyProgramDiscount}%)`,
              amount: loyaltyProgramDiscountAmount,
            }}
          />
        ) : null}
        {params.loyaltyDiscount ? (
          <ChargesRow
            charge={{
              description: `Less loyalty discount (${params.loyaltyDiscount}%)`,
              amount: loyaltyDiscountAmount,
            }}
          />
        ) : null}
        <ChargesRow
          strikeThrough={!params.autoCharge}
          charge={{
            description: `Less registered credit card discount (${autoChargeDiscount}%)`,
            amount: autoChargeDiscountAmount,
          }}
        />
        <ChargesRow
          bold
          charge={{
            description: "Total",
            amount: total,
          }}
        />
      </Table.Body>
    </Table>
  )
}

interface ChargesProp {
  charges: ChargesFragment | null
}

function ChargesWithQuery(props: OuterProps) {
  // If we are called when updating an existing booking, send the bookingId in the query.
  const {booking, journeyId, bookingId} = props
  return (
    <GraphQL.Query
      query={CHARGES}
      // We only need cache-and-network here because the journey distance might be changed
      // independently by admin...
      fetchPolicy="cache-and-network"
      variables={{details: booking, journeyId, bookingId}}
      render={({data, loading}) => {
        if (data) {
          return <Charges {...props} charges={data.charges} loading={loading} />
        }
        return <Loader inline active />
      }}
    />
  )
}

export {ChargesWithQuery as Charges}



// WEBPACK FOOTER //
// ./src/components/Charges.tsx